import {
  useDisburseEncashmentMutation,
  useGetDisbursableEmployeesQuery,
} from '@/app/features/employee/emp-payroll';
import CustomDrawer from '@/components/CustomDrawer';
import EmployeeSelect, {
  type EmployeeMinimal,
} from '@/components/EmployeeSelect';
import { Calendar03Icon } from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, DatePicker, Flex, Form } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { flattenAssigned } from '../shared/payroll.helpers';

type Props = {
  open: boolean;
  /** The encashment batch being paid out. */
  batchId: string;
  handleClose: () => void;
};

type FormValues = {
  month: Dayjs;
  assigned_employees?: Record<string, string[]>;
};

/**
 * Disburse to Payroll: moves the chosen employees' encashment into a payroll
 * month, where it appears as an addition on their salary.
 */
const ManageDisburse = ({ open, batchId, handleClose }: Props) => (
  <CustomDrawer
    width={800}
    open={open}
    onClose={handleClose}
    title="Disburse to Payroll"
  >
    {/* Mounted only while open, so every disbursement starts clean. */}
    {open && <DisburseForm batchId={batchId} onDone={handleClose} />}
  </CustomDrawer>
);

const DisburseForm = ({
  batchId,
  onDone,
}: {
  batchId: string;
  onDone: () => void;
}) => {
  const [form] = Form.useForm<FormValues>();
  const assignedEmployees = Form.useWatch('assigned_employees', form);

  // Only employees of this batch who still have something to disburse.
  const { data: employeeData } = useGetDisbursableEmployeesQuery(
    { id: batchId, args: { params: { status: 'active' } } },
    { skip: !batchId },
  );
  const employees = (employeeData?.data?.data || []) as EmployeeMinimal[];

  const [disburseEncashment, { isLoading: isDisbursing }] =
    useDisburseEncashmentMutation();

  const handleSubmit = async (values: FormValues) => {
    const userIds = flattenAssigned(values.assigned_employees);
    if (!userIds.length) {
      form.setFields([
        { name: 'assigned_employees', errors: ['Please select employees'] },
      ]);
      return;
    }

    try {
      await disburseEncashment({
        batch_id: batchId,
        year: values.month.year(),
        month: values.month.format('MM'),
        user_ids: userIds,
      }).unwrap();
      toast('success', 'Amount disbursed to payroll successfully');
      onDone();
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to disburse encashment'));
    }
  };

  return (
    <Form
      form={form}
      size="large"
      layout="vertical"
      className="custom-form space-y-6"
      onFinish={handleSubmit}
    >
      <Form.Item initialValue={{}} noStyle name="assigned_employees" />

      <div className="grid grid-cols-2 gap-6">
        <Form.Item
          label="Payroll Month"
          name="month"
          rules={[{ required: true, message: 'Please select month' }]}
        >
          {/* A payroll month that has not started cannot be paid into. */}
          <DatePicker
            picker="month"
            format="MMMM YYYY"
            placeholder="Select month"
            className="w-full"
            suffixIcon={<Calendar03Icon className="text-white" />}
            disabledDate={(current) =>
              current && current.isAfter(dayjs().endOf('month'))
            }
          />
        </Form.Item>
      </div>

      <EmployeeSelect form={form} employees={employees} />
      <EmployeeSelect.Selected
        form={form}
        employees={employees}
        allowRemoveEmployee
        assignedEmployees={assignedEmployees || {}}
      />

      <Flex justify="end">
        <Button
          htmlType="submit"
          loading={isDisbursing}
          className="btn-primary min-w-[80px] h-11 font-semibold"
        >
          Disburse
        </Button>
      </Flex>
    </Form>
  );
};

export default ManageDisburse;
